import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { Icon, IconName } from '../icon/icon';
import { ThemePreference, ThemeService } from './theme.service';

const ICONS: Record<ThemePreference, IconName> = {
  light: 'sun',
  dark: 'moon',
  system: 'monitor',
};

const LABELS: Record<ThemePreference, string> = {
  light: 'light',
  dark: 'dark',
  system: 'system',
};

/**
 * Icon button that cycles the theme preference: light → dark → system.
 * The icon reflects the current preference; the accessible name announces it.
 */
@Component({
  selector: 'ui-theme-toggle',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 0;
      border-radius: var(--radius-200);
      background: transparent;
      color: var(--icon-default-secondary);
      cursor: pointer;

      &:hover {
        background: var(--background-default-default-hover);
        color: var(--icon-default-default);
      }
    }
  `,
  template: `
    <button
      type="button"
      [attr.aria-label]="label()"
      [attr.title]="label()"
      (click)="theme.cyclePreference()"
    >
      <ui-icon [name]="icon()" />
    </button>
  `,
})
export class ThemeToggle {
  protected readonly theme = inject(ThemeService);

  protected readonly icon = computed(() => ICONS[this.theme.preference()]);
  protected readonly label = computed(
    () => `Theme: ${LABELS[this.theme.preference()]} (select to change)`,
  );
}
